import { makeStyles } from '@material-ui/styles'
import StatsCell from 'app/plugins/infrastructure/components/common/cells/StatsCell'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { getMigrationsSince } from 'openstack/components/vms/migrations/actions'
import { listHypervisors, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import {
  hypervisorsSelector,
  resmgrHostsSelector,
  UNASSIGNED_CLUSTER,
} from 'openstack/components/infrastructure/selectors'
import { IpAddressCellComponent } from 'openstack/components/infrastructure/hosts/HostsListPage'
import { uniq, without } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { migrateVirtualMachine } from './actions'
import { AFFINITY_POLICIES_DOC_LINK } from './helpers'
import { AFFINITY_POLICIES } from './server-groups/PolicyPicklist'

const searchTargets = ['name', 'id', 'readableInterfaces']

const hostColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'ID',
    display: false,
  },
  {
    id: 'clusterName',
    label: 'Cluster',
  },
  {
    id: 'info.responding',
    label: 'Responding',
    render: (value) => (value === undefined ? 'Unknown' : value ? 'True' : 'False'),
  },
  {
    id: 'readableInterfaces',
    label: 'IP Address',
    render: (value) => value?.map((iface) => <div key={iface}>{iface}</div>),
  },
  {
    id: 'usage',
    label: 'Resource Utilization',
    render: (value, item) => <StatsCell value={value} item={item} />,
  },
]

export default function MigrateVirtualMachineDialog({ rows: vms, onClose }) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)
  const [showAllHosts, setShowAllHosts] = useState(false)
  const [noMatchingHosts, setNoMatchingHosts] = useState(false)
  const isAdminUser = isAdmin()

  const { error, update: migrateFn, updating } = useUpdateAction(migrateVirtualMachine)
  const title = `Migrate ${vms.length > 1 ? 'VMs' : 'VM'}`
  const vmHostIds = useMemo(() => {
    return uniq(vms.map((vm) => vm['OS-EXT-SRV-ATTR:host']))
  }, [vms])
  const defaultParams = {
    hosts: [],
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingHosts } = useListAction(listResmgrHosts, {
    params: {},
  })
  const hosts = useSelectorWithParams(resmgrHostsSelector, {})

  const { loading: loadingHypervisors } = useListAction(listHypervisors, {
    params: {},
  })
  const hypervisors = useSelectorWithParams(hypervisorsSelector, {})

  const { update: pollMigrations } = useUpdateAction(getMigrationsSince)

  // Get cluster from first first VM
  const hostCluster = vms[0]?.clusterName

  // Check if any of the VMs are part of a soft-affinity group
  const softAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.SOFT_AFFINITY
  }, [vms])

  const hardAntiAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.HARD_ANTI_AFFINITY
  }, [vms])

  const softAntiAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.SOFT_ANTI_AFFINITY
  }, [vms])

  const hypervisorHosts = useMemo(() => {
    if (loadingHosts || loadingHypervisors) {
      return []
    }

    // Filter hypervisors which have scheduling disabled
    const hypervisorIds = hypervisors.flatMap((hypervisor) => {
      if (hypervisor?.status === 'enabled') {
        return [hypervisor?.service?.host]
      }
      return []
    })
    const validHypervisorIds = without(vmHostIds, hypervisorIds)

    const filteredHosts = hosts.filter(
      (host) =>
        validHypervisorIds.includes(host?.id) &&
        (hostCluster === UNASSIGNED_CLUSTER || host?.clusterName === hostCluster),
    )

    // Filter out hosts that have a vms from the same server group
    if ((hardAntiAffinityVm || softAntiAffinityVm) && !showAllHosts) {
      const matchingHosts = filteredHosts?.filter((host) => {
        return host?.vms?.every((vm) => vm?.serverGroup?.name !== vms[0]?.serverGroup?.name)
      })
      setNoMatchingHosts(matchingHosts?.length === 0)
      return matchingHosts
    }

    return filteredHosts
  }, [
    hosts,
    hypervisors,
    loadingHosts,
    loadingHypervisors,
    vmHostIds,
    hostCluster,
    showAllHosts,
    hardAntiAffinityVm,
    softAntiAffinityVm,
  ])

  const handleSubmit = useCallback(() => {
    const asyncActions = async () => {
      let isSuccess = true
      const successfulVms = []
      for (const vm of vms) {
        try {
          const body = ['shutoff', 'stopped'].includes(vm?.state)
            ? {
                migrate: {
                  host: isAdminUser ? params.hosts[0]?.id : null,
                },
              }
            : {
                'os-migrateLive': {
                  host: isAdminUser ? params.hosts[0]?.id : null,
                  block_migration: 'auto',
                },
              }

          const { success } = await migrateFn({ id: vm.id, body })
          successfulVms.push(vm)
          isSuccess = success
        } catch (e) {
          isSuccess = false
        }
      }

      pollMigrations()
      if (isSuccess) {
        onClose(true)
      }
      setSubmitting(false)
    }
    setSubmitting(true)
    asyncActions()
  }, [params, vms, isAdminUser])

  return (
    <ModalForm
      title={title}
      onSubmit={handleSubmit}
      disableSubmit={(softAntiAffinityVm || hardAntiAffinityVm) && !params.hosts?.length}
      onClose={onClose}
      loading={loadingHosts || loadingHypervisors}
      submitting={submitting}
      error={error}
      submitTitle={title}
      panel="dialog"
      maxWidth={isAdminUser ? 1200 : 600}
      open
    >
      <div className={classes.container}>
        <Text variant="body2">
          You are about to migrate the following VMs:{' '}
          <b>{vms.map((vm) => vm.name || vm.id).join(', ')}</b>
        </Text>
        {isAdminUser ? (
          <>
            <Text variant="body2">
              Select the hypervisor you would like to migrate the selected VMs to
            </Text>
            <Text variant="body2">
              <b>Note:</b> Hypervisors hosting a selected VM will not be displayed in the list
              below.
            </Text>
            {(softAffinityVm || softAntiAffinityVm || hardAntiAffinityVm) && (
              <Alert variant="warning" title="Warning">
                {softAffinityVm && (
                  <Text variant="body2">
                    This virtual machine is part of a soft affinity group. Moving the VM may break
                    the affinity policy.{' '}
                    <ExternalLink url={AFFINITY_POLICIES_DOC_LINK}>
                      <strong>More info</strong>
                    </ExternalLink>
                  </Text>
                )}
                {(softAntiAffinityVm || hardAntiAffinityVm) && (
                  <Text variant="body2">
                    This virtual machine is part of a {softAntiAffinityVm ? 'soft' : 'hard'}{' '}
                    anti-affinity group.{' '}
                    {!noMatchingHosts
                      ? 'Below is a filtered list of hypervisors that the virtual machine could migrate to without breaking the policy.'
                      : `There are no suitable hypervisors in this cluster that this VM can migrate to without breaking the policy. ${
                          softAntiAffinityVm
                            ? 'If you still want to migrate this VM, then click on Show all hosts and select a host from the list where you want to migrate this VM.'
                            : ''
                        }`}
                    <ExternalLink url={AFFINITY_POLICIES_DOC_LINK}>
                      {' '}
                      <strong>More info</strong>
                    </ExternalLink>
                  </Text>
                )}
              </Alert>
            )}
            <ListTableField
              id="hosts"
              data={hypervisorHosts}
              loading={loadingHosts || loadingHypervisors}
              columns={hostColumns}
              onChange={getParamsUpdater('hosts')}
              value={params.hosts}
              uniqueIdentifier="id"
              searchTargets={searchTargets}
              emptyText={
                softAntiAffinityVm || hardAntiAffinityVm
                  ? `This virtual machine is part of a ${
                      softAntiAffinityVm ? 'soft' : 'hard'
                    } anti-affinity group. There are no suitable hypervisors in this cluster that this VM can migrate to without breaking the policy`
                  : 'No available hypervisors found'
              }
              extraToolbarContent={
                softAntiAffinityVm ? (
                  <ToggleSwitch
                    active={showAllHosts}
                    onClick={() => setShowAllHosts(!showAllHosts)}
                    label="Show all hosts"
                    data-testid="show-all-hosts-toggle-switch"
                    className={classes.toggleSwitch}
                  />
                ) : null
              }
              required
            />
          </>
        ) : (
          <Text variant="body2">A new hypervisor will be selected automatically.</Text>
        )}
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 24,
  },
  toggleSwitch: {
    margin: theme.spacing(0, 2),
  },
}))
